import { ArrowRightIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { Button, Modal } from 'ui-toolkit-tailwind/src/components'

import HoldToConfirmButton from './HoldToConfirmButton'

const formatTimestamp = value => {
	if (!value) return 'Not recorded'

	return new Intl.DateTimeFormat('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
		timeZoneName: 'short'
	}).format(new Date(value))
}

const latestRunLabel = status => {
	switch (status) {
	case 'success':
		return 'Success'
	case 'failed':
		return 'Failed'
	default:
		return 'Not recorded'
	}
}

const detailRows = details => [
	{
		label: 'Last successful email send',
		value: formatTimestamp(details?.lastSuccessfulEmailSentAt)
	},
	{
		label: 'Next scheduled email send',
		value: formatTimestamp(details?.nextScheduledSendAt)
	},
	{
		label: 'Most recent scheduled run',
		value: latestRunLabel(details?.latestScheduledRun?.status)
	}
]

const choices = [
	{ policy: 'wait_all', label: 'Wait & Send when all Locations are Updated', description: 'Hold unsent reports until every remaining location has complete data.' },
	{ policy: 'ready_then_wait', label: 'Send Updated Locations Now & Send Remaining When Update Complete', description: 'Send ready reports now. Check every five minutes and send the remaining reports as their data becomes complete.' },
	{ policy: 'ready_cancel', label: 'Send Updated Locations Now & Cancel Remaining', description: 'Send ready reports now. Cancel the pending locations for this report date only.' }
]
const reasonLabels = {
	missing_data: 'Missing data', all_zero_data: 'All figures are zero; verification required',
	missing_machines: 'Incomplete machine data', invalid_financial_data: 'Incomplete financial figures'
}

const ManualEmailSendModal = ({ visible, details, preview, previewLoading, previewError, activeAction, onClose, onSend, onSendAndNotify, onChoose, onUpdateData }) => {
	const [notifyAdmins, setNotifyAdmins] = useState(false)
	const hasPending = (preview?.delayedLocations?.length || 0) > 0
	const blocked = previewLoading || !!previewError || !preview
	return (
		<Modal
			visible={visible}
			onClose={activeAction ? () => {} : onClose}
			size="2xl"
			panelClassName="sm:max-w-[40rem]"
			clickOutsideToClose={!activeAction}
	>
			<Modal.Header>Email Manually</Modal.Header>
			<Modal.Body>
				<div className="space-y-5">
					{previewLoading && <p role="status" className="text-sm text-gray-600">Checking each location for complete VGT data...</p>}
					{previewError && <p role="alert" className="text-sm text-red-700">{previewError}</p>}
					{hasPending && (
					<div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
						<h3 className="font-semibold text-amber-900">Locations Awaiting Updated VGT Data</h3>
						<p className="mt-1 text-sm text-gray-700">Report date: {preview.reportDate}. {preview.readyLocationCount} location(s) have unsent reports ready.</p>
						<ul className="mt-3 space-y-2 text-sm">
							{preview.delayedLocations.map(location => (
								<li key={location.id}><strong>{location.name}</strong><span className="block text-gray-600">{reasonLabels[location.reason] || 'Data verification pending'}</span></li>
							))}
						</ul>
						<p className="mt-3 text-xs text-gray-600">Your choice applies to reports still unsent for this date. Reports already sent or queued will not be duplicated.</p>
					</div>
				)}
					<p className="text-sm text-gray-600">
						Choose how to send reports when VGT data is incomplete. Available reports can also correct earlier reports that contained incomplete data.
					</p>

					<div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
						{detailRows(details).map(row => (
							<div key={row.label} className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
								<p className="text-xs font-semibold uppercase tracking-wide text-amber-900">{row.label}</p>
								<p className="text-sm text-gray-700 sm:max-w-[15rem] sm:text-right">{row.value}</p>
							</div>
					))}

						{details?.latestScheduledRun?.status === 'failed' && (
						<div className="border-t border-amber-200 pt-3">
							<p className="text-xs font-semibold uppercase tracking-wide text-amber-900">Failure reason</p>
							<p className="mt-1 text-sm text-gray-700">
								{details.latestScheduledRun.failureReason || 'No failure reason was recorded.'}
							</p>
						</div>
					)}
					</div>

					<p className="text-xs text-gray-500">
						Send Emails &amp; Notify Admin also queues a separate admin summary. An admin notification does not confirm report delivery.
					</p>
				</div>
			</Modal.Body>
			<Modal.Footer>
				{hasPending ? (
					<div className="space-y-3 px-4 py-5 sm:px-6">
						<label className="flex items-center gap-2 text-sm text-gray-700">
							<input type="checkbox" checked={notifyAdmins} onChange={event => setNotifyAdmins(event.target.checked)} disabled={!!activeAction} />
							Also Notify Admin
						</label>
						{choices.map(choice => (
							<HoldToConfirmButton
								key={choice.policy} id={`manual-${choice.policy}`} label={choice.label}
								loadingLabel="Saving choice..." holdLabel="Keep Holding..."
								helperText={choice.description} holdingHelperText="Keep holding to confirm this choice."
								busy={activeAction === choice.policy} disabled={blocked || (!!activeAction && activeAction !== choice.policy)}
								onConfirm={() => onChoose(choice.policy, notifyAdmins)}
								className="min-h-[44px] w-full whitespace-normal bg-[#3E63F4] px-3 text-white hover:bg-[#0b2986]"
								progressClassName="bg-[#0b2986]/90" icon={ArrowRightIcon} wrapperClassName="w-full"
						/>
					))}
						<Button type="button" intent="secondary" onClick={onUpdateData} disabled={!!activeAction} className="w-full whitespace-normal">
							Update VGT Data Manually
						</Button>
						<Button type="button" intent="secondary" onClick={onClose} disabled={!!activeAction} className="w-full">Cancel</Button>
					</div>
			) : <div className="flex flex-col-reverse gap-3 px-4 py-5 sm:flex-row sm:items-start sm:justify-end sm:px-6">
				<Button
					type="button"
					intent="secondary"
					onClick={onClose}
					disabled={!!activeAction}
					className="inline-flex w-full items-center justify-center px-4 py-2 text-sm sm:w-[11.5rem]"
				>
					Cancel
				</Button>
				<HoldToConfirmButton
					id="manual-email-send"
					label="Send Emails"
					loadingLabel="Sending..."
					holdLabel="Keep Holding..."
					helperText="Press and hold to send."
					holdingHelperText="Keep holding to send the emails."
					busy={activeAction === 'send'}
					disabled={blocked || (!!activeAction && activeAction !== 'send')}
					onConfirm={onSend}
					className="min-h-[38px] bg-[#3E63F4] text-white hover:bg-[#0b2986] focus-visible:outline-[#3E63F4]"
					progressClassName="bg-[#0b2986]/90"
					icon={ArrowRightIcon}
					wrapperClassName="sm:w-[11.5rem]"
				/>
				<HoldToConfirmButton
					id="manual-email-send-notify"
					label="Send Emails & Notify Admin"
					loadingLabel="Sending..."
					holdLabel="Keep Holding..."
					helperText="Press and hold to send and notify."
					holdingHelperText="Keep holding to send and notify the administrator."
					busy={activeAction === 'send_and_notify'}
					disabled={blocked || (!!activeAction && activeAction !== 'send_and_notify')}
					onConfirm={onSendAndNotify}
					className="min-h-[38px] bg-[#0ea62d] text-white hover:bg-[#05721b] focus-visible:outline-[#0ea62d]"
					progressClassName="bg-[#05721b]/90"
					icon={ArrowRightIcon}
					wrapperClassName="sm:w-[11.5rem]"
				/>
			</div>}
			</Modal.Footer>
		</Modal>
)
}

ManualEmailSendModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	details: PropTypes.shape({
		lastSuccessfulEmailSentAt: PropTypes.string,
		nextScheduledSendAt: PropTypes.string,
		latestScheduledRun: PropTypes.shape({
			status: PropTypes.string,
			failureReason: PropTypes.string
		})
	}),
	preview: PropTypes.object,
	previewLoading: PropTypes.bool,
	previewError: PropTypes.string,
	onChoose: PropTypes.func.isRequired,
	onUpdateData: PropTypes.func.isRequired,
	activeAction: PropTypes.oneOf([null, 'send', 'send_and_notify', 'wait_all', 'ready_then_wait', 'ready_cancel']),
	onClose: PropTypes.func.isRequired,
	onSend: PropTypes.func.isRequired,
	onSendAndNotify: PropTypes.func.isRequired
}

ManualEmailSendModal.defaultProps = {
	details: null
}

export default ManualEmailSendModal
